import { batch, computed, signal } from "@preact/signals-react"
import { encodingJSON, getPreviousDate, isEmpty, stringToDate } from "@ui-seeker/utils"

import { DATE_METRICS, DATE_PERIODS, GROUPED_BY, KPI_PRICING, KPI_TYPE } from "@constants"
import { favFilterSelected } from "@signals/user"
import { currentPeriod, currentSingleDate, currentSingleDateStr, dateRange, dateRangePeriod, endDate, prevEndDate, prevSingleDate, prevSingleDateStr, prevStartDate, startDate} from "./date"
import { authorizedSeller, buyBox, groupedBy, includeShippingPrice, kpiPrice, splitKpi, validated, vision , normalized, kpiType} from "./kpi"
import { brand, category, categoryFake, googleCategory, sku, skuGroup, lostBuyBoxReason, productsInStock, company } from "./product"
import {country, keyword, retailer, dashboard, seller, secondRetailer} from "./sites"
import { tags, pageCategory, pageProductType, resourceType, deviceType, sectionCategory } from "./searchType"
import { metric } from "../date"


export const isLoadingCommonFilters = signal(false)
export const isSystemBusy = signal(false)
export const errors = signal({})
export const disabledCommonFiltersSubmit = signal(false)
export const haveEncodedValue = signal(false)
export const haveSkuEncodedValue = signal(false)
export const submittedFilters = signal({})
export const customerSelectedID = signal(null)
export const triggerCommonFiltersSubmit = signal(0)

// Computed
export const haveErrors = computed(() => !isEmpty(Object.fromEntries(Object.entries(errors.value).filter(([_, v]) => v !== null))))
export const commonFiltersValue = computed(() => ({
    startDate: startDate.value,
    endDate: endDate.value,
    prevStartDate: prevStartDate.value,
    prevEndDate: prevEndDate.value,
    currentSingleDate: currentSingleDateStr.value,
    prevSingleDate: prevSingleDateStr.value,
    brand: brand.value,
    company: company.value,
    currentPeriod: currentPeriod.value,
    category: category.value,
    googleCategory: googleCategory.value,
    country: country.value,
    sectionCategory: sectionCategory.value,
    sku: sku.value,
    skuGroup: skuGroup.value,
    retailer: retailer.value,
    secondRetailer: secondRetailer.value,
    kpiPrice: kpiPrice.value,
    groupedBy: groupedBy.value,
    includeShippingPrice: includeShippingPrice.value,
    buyBox: buyBox.value,
    authorizedSeller: authorizedSeller.value,
    keyword: keyword.value,
    dashboard: dashboard.value,
    categoryFake: categoryFake.value,
    sellers: seller.value,
    vision: vision.value,
    validated: validated.value,
    normalized: normalized.value,
    splitKpi: splitKpi.value,
    metric: metric.value,
    lostBuyBoxReason: lostBuyBoxReason.value,
    productsInStock: productsInStock.value,
    kpiType: kpiType.value,
    tags: tags.value,
    pageCategory: pageCategory.value,
    pageProductType: pageProductType.value,
    resourceType: resourceType.value,
    deviceType: deviceType.value,
    customerSelectedID: customerSelectedID.value
}))

export const commonFiltersShareValue = computed(() =>
    Object.entries(commonFiltersValue.value).reduce((acc, [key, value]) => {
        if (value === undefined || value === null || value === '') return acc
        if (Array.isArray(value) && !value.length) return acc
        return { ...acc, [key]: value }
    }, {
        currentPeriod: currentPeriod.value
    })
)

export const dynamicCommonFiltersValue = computed(() => {

    const filters = {
        startDate: startDate.value,
        endDate: endDate.value,
        prevStartDate: prevStartDate.value,
        prevEndDate: prevEndDate.value,
        groupedBy: groupedBy.value,
        productsInStock: productsInStock.value,
        withShippingPrice: includeShippingPrice.value,
        metric: metric.value,
        ...brand.value.length && {
            brand: brand.value
        },
        ...category.value.length && {
            category: category.value
        },
        ...sku.value.length && {
            sku: sku.value
        },
        ...!!skuGroup.value && {
            skuGroup: skuGroup.value
        },
        ...retailer.value.length && {
            retailer: retailer.value
        },
        ...country.value.length && {
            country: country.value
        }
    }
    return filters
})


// Functions
export const encodeCommonFiltersShareValue = (filters={}, launch=true) => `${encodingJSON(filters)}&launch=${launch}`

export const setCommonFilters = filters => batch(() => {
    currentPeriod.value = filters.currentPeriod || DATE_PERIODS.YESTERDAY
    currentSingleDate.value = !!filters.currentSingleDate ? stringToDate(filters.currentSingleDate) :  getPreviousDate({ days: 1 })
    prevSingleDate.value = stringToDate(filters.prevSingleDate)
    dateRange.value = dateRangePeriod.value
    if (currentPeriod.value === DATE_PERIODS.CUSTOM)
        dateRange.value = [stringToDate(filters.startDate), stringToDate(filters.endDate)]
    prevStartDate.value = filters.prevStartDate
    prevEndDate.value = filters.prevEndDate
    brand.value = filters.brand || []
    company.value = filters.company || []
    category.value = filters.category || []
    googleCategory.value = filters.googleCategory || []
    country.value = filters.country || []
    sectionCategory.value = filters.sectionCategory || []
    sku.value = filters.sku || []
    skuGroup.value = filters.skuGroup || null
    retailer.value = filters.retailer || []
    secondRetailer.value = filters.secondRetailer || null
    kpiPrice.value = filters.kpiPrice || KPI_PRICING.ACTUAL
    includeShippingPrice.value = filters.includeShippingPrice ?? false
    buyBox.value = filters.buyBox ?? false
    authorizedSeller.value = filters.authorizedSeller ?? false
    keyword.value = filters.keyword ?? []
    dashboard.value = filters.dashboard ?? []
    categoryFake.value = filters.categoryFake ?? []
    seller.value = filters.sellers ?? []
    splitKpi.value = filters.splitKpi || [GROUPED_BY.RETAILER]
    vision.value = filters.vision || GROUPED_BY.COUNTRY
    validated.value = validated.value || [0,1]
    normalized.value = normalized.value || [0,1]
    metric.value = filters.metric
    lostBuyBoxReason.value = filters.lostBuyBoxReason || []
    productsInStock.value = filters.productsInStock || false
    kpiType.value = filters.kpiType
    tags.value = filters.tags || []
    pageCategory.value = filters.pageCategory || []
    pageProductType.value = filters.pageProductType || []
    resourceType.value = filters.resourceType || []
    deviceType.value = filters.deviceType || ''
})

export const resetCommonFilters = () => batch(() => {
    currentSingleDate.value = getPreviousDate({ days: 1 })
    prevSingleDate.value = getPreviousDate({ days: 1 }, currentSingleDate.value)
    dateRange.value = [ getPreviousDate({ days: 1 }), getPreviousDate({ days: 1 }) ]
    currentPeriod.value = DATE_PERIODS.YESTERDAY
    brand.value = []
    company.value = []
    category.value = []
    googleCategory.value = []
    country.value = []
    sectionCategory.value = []
    sku.value = []
    skuGroup.value = null
    retailer.value = []
    secondRetailer.value = null
    kpiPrice.value = KPI_PRICING.ACTUAL
    includeShippingPrice.value = false
    buyBox.value = false
    authorizedSeller.value = false
    keyword.value = []
    dashboard.value = []
    categoryFake.value = []
    seller.value = []
    vision.value = GROUPED_BY.COUNTRY
    splitKpi.value = [GROUPED_BY.RETAILER]
    metric.value = DATE_METRICS.MONTHLY
    validated.value = [0,1]
    normalized.value = [0,1]
    favFilterSelected.value = null
    lostBuyBoxReason.value = []
    productsInStock.value = false
    kpiType.value = KPI_TYPE.ACTUAL_PRICE
    tags.value = []
    pageCategory.value = []
    pageProductType.value = []
    resourceType.value = []
    deviceType.value = ''
})